/**
 * Browser settings that the BFF may override at run time, so one built image can serve any origin.
 * The BFF reads each key from a `BROWSER_<key>` environment variable and serves the non-empty values
 * through `/runtime-config.js`; the frontend falls back to the build-time `import.meta.env` value.
 * Everything listed here is sent to the browser, so never add secrets.
 */
export const runtimeConfigKeys = [
  'VITE_API_URL',
  'VITE_APPLICATION_INSIGHTS_CONNECTION_STRING',
  'VITE_BUILDING_PORTAL_URI',
  'VITE_KEYPOINT_API_URI',
  'VITE_KEYPOINT_CLIENT_ID',
  'VITE_KEYPOINT_IDP_URL',
  'VITE_SIGNALR_HUBS_URI',
  'VITE_SYNCFUSION_KEY',
  'VITE_TRPC_URL',
] as const;

export type RuntimeConfigKey = (typeof runtimeConfigKeys)[number];

export type RuntimeConfig = { [key in RuntimeConfigKey]?: string };

/** Prefix of the BFF environment variables that hold the runtime browser settings. */
export const runtimeConfigEnvPrefix = 'BROWSER_';

/**
 * Keeps only the known runtime config keys with a non-empty string value.
 *
 * @param source - Candidate values, keyed by runtime config key.
 * @returns The known keys that have a non-empty string value.
 */
export const pickRuntimeConfig = (
  source: { [key: string]: unknown } | undefined,
): RuntimeConfig => {
  const config: RuntimeConfig = {};

  runtimeConfigKeys.forEach((key) => {
    const value = source?.[key];

    if (typeof value === 'string' && value.trim() !== '') {
      config[key] = value;
    }
  });

  return config;
};
